// Reusable Timesheet PDF generator with switchable templates
// Requires: jspdf, jspdf-autotable (installed in Frontend)

import logoImage from '../assets/logo.png';

export async function exportTimesheetPdf(timesheet, options = {}) {
  const {
    template = 'classic',
    fileName,
  } = options;

  const JSPDFCtor = await loadJsPDF();
  await ensureAutoTableLoaded();
  const doc = new JSPDFCtor({ unit: 'pt', format: 'a4' });
  
  // Load logo as base64 data URL
  const logoDataUrl = await loadLogoAsDataUrl(logoImage);

  if (template === 'compact') {
    renderCompact(doc, timesheet, logoDataUrl);
  } else {
    renderClassic(doc, timesheet, logoDataUrl);
  }

  // Format: TIMESHEET_employee name_DD.MM.YYYY.pdf
  const employeeName = `${timesheet?.employee?.firstName || ''} ${timesheet?.employee?.lastName || ''}`.trim() || 'Employee';
  const endDate = new Date(timesheet?.endDate || timesheet?.startDate);
  const day = String(endDate.getDate()).padStart(2, '0');
  const month = String(endDate.getMonth() + 1).padStart(2, '0');
  const year = endDate.getFullYear();
  const dateStr = `${day}.${month}.${year}`;
  const defaultName = `TIMESHEET_${employeeName}_${dateStr}.pdf`;
  doc.save(fileName || defaultName);
}

async function loadJsPDF() {
  try {
    const m = await import('jspdf');
    return m.jsPDF || m.default;
  } catch (e1) {
    const m2 = await import('jspdf/dist/jspdf.umd.min.js');
    return m2.jsPDF || m2.default;
  }
}

async function ensureAutoTableLoaded() {
  // ESM build exposes the plugin at the package root
  await import('jspdf-autotable');
}

async function loadLogoAsDataUrl(imageSrc) {
  try {
    // Fetch the image as a blob
    const response = await fetch(imageSrc);
    const blob = await response.blob();
    
    // Convert blob to base64 data URL
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Failed to load logo image:', error);
    return null;
  }
}

function safeName(timesheet) {
  const fn = timesheet?.employee?.firstName || '';
  const ln = timesheet?.employee?.lastName || '';
  const n = `${fn} ${ln}`.trim() || 'Employee';
  return n.replace(/\s+/g, '_');
}

function safeDate(d) { return new Date(d).toISOString().slice(0, 10); }

// Shared helpers
function buildWeek(timesheet) {
  const start = new Date(timesheet.startDate);
  const end = new Date(timesheet.endDate);
  const days = [];
  for (let dt = new Date(start); dt <= end; dt = new Date(dt.getTime() + 24*60*60*1000)) {
    days.push(new Date(dt));
  }
  return days;
}

function calcDayTotal(entries, day) {
  return (entries || [])
    .filter(e => e.date && new Date(e.date).toDateString() === day.toDateString())
    .reduce((s, e) => s + (normalizeHours(e)), 0);
}

function listProjects(timesheet) {
  return Array.from(new Set((timesheet?.entries || []).map(e => e.project || '').filter(p => p)));
}

// Constant for Y position where content starts after the header
const CONTENT_START_Y = 230; // In pt

// Function to draw the footer with approver info in bottom right corner
function drawPageFooter(doc, timesheet) {
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const right = pageWidth - 40;
  const bottom = pageHeight - 20;
  
  // Only show if timesheet is approved
  if (timesheet?.approvedBy && timesheet?.approvedAt) {
    const approverName = `${timesheet.approvedBy.firstName || ''} ${timesheet.approvedBy.lastName || ''}`.trim() || 'N/A';
    const approvedDate = new Date(timesheet.approvedAt);
    const approvedDateStr = approvedDate.toLocaleDateString();
    const approvedTimeStr = approvedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(20); // Much darker text (was 100, now 20 for better visibility)
    
    // First line: "Approved by: [name]"
    const approvedByText = `Approved by: ${approverName}`;
    doc.text(approvedByText, right, bottom - 18, { align: 'right' });
    
    // Second line: Date and time (increased gap between lines)
    const dateTimeText = `${approvedDateStr} at ${approvedTimeStr}`;
    doc.text(dateTimeText, right, bottom, { align: 'right' });
  }
}

// Function to add page numbers to all pages (only if multiple pages exist)
function addPageNumbers(doc, totalPages) {
  if (totalPages <= 1) return; // Don't add page numbers for single page
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const center = pageWidth / 2;
  const bottom = pageHeight - 20;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(20); // Dark text
  
  // Add page numbers to all pages
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const pageNumberText = `Page ${i} of ${totalPages}`;
    doc.text(pageNumberText, center, bottom, { align: 'center' });
  }
}

// Function to draw the full page header (logo, company info, title, meta info)
function drawPageHeader(doc, timesheet, logoDataUrl) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const left = 40;
  const right = pageWidth - 40;
  const mm = (v) => v; // use pt

  // Add logo in top right corner
  if (logoDataUrl) {
    try {
      const logoWidth = 110; // Width in points
      const logoHeight = 55; // Height in points (maintain aspect ratio)
      const logoX = pageWidth - logoWidth - 40; // Right margin
      const logoY = 60; // Top margin
      doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
    } catch (error) {
      console.warn('Failed to add logo to PDF:', error);
    }
  }

  // Header brand line
  doc.setFillColor(9, 109, 217);
  doc.rect(left, mm(50), right - left, 2, 'F');

  // Company
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(58, 145, 136); // #3A9188
  doc.setFontSize(16);
  doc.text('Primesource Consulting LLP', left, mm(80));
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75); // Darker for better visibility
  doc.setFontSize(10);
  // Address with increased line spacing
  let addressY = mm(100);
  doc.text('10/91/K6, Nehru Nager,', left, addressY);
  addressY += 14; // Increased spacing between lines
  doc.text('Surandai Road, Pavoorchatram', left, addressY);
  addressY += 14; // Increased spacing between lines
  doc.text('Tenkasi District - 627806', left, addressY);

  // Title
  doc.setTextColor(35, 56, 118);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text('Timesheet', left, mm(165));

  // Meta
  const employeeName = `${timesheet?.employee?.firstName || ''} ${timesheet?.employee?.lastName || ''}`.trim() || 'Employee';
  const employeeCode = timesheet?.employee?.employeeCode || '-';
  const timeframe = `Week of ${new Date(timesheet.startDate).toLocaleDateString()} - ${new Date(timesheet.endDate).toLocaleDateString()}`;
  const submitted = timesheet?.submittedAt ? new Date(timesheet.submittedAt).toLocaleDateString() : '-';

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(20); // Darker for better visibility
  doc.text('Name', left, mm(200));
  doc.text('Employee Id', left + mm(140), mm(200));
  doc.text('Timeframe', left + mm(250), mm(200));
  doc.text('Submitted on', left + mm(440), mm(200));
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(45); // Darker for better visibility
  
  // Wrap long employee name to multiple lines if needed
  const nameMaxWidth = mm(120); // Maximum width for name column
  const nameLines = doc.splitTextToSize(employeeName, nameMaxWidth);
  let nameY = mm(216);
  nameLines.forEach((line, idx) => {
    doc.text(line, left, nameY + (idx * 12));
  });
  
  doc.text(employeeCode, left + mm(140), mm(216));
  doc.text(timeframe, left + mm(250), mm(216));
  doc.text(submitted, left + mm(440), mm(216));
}

function renderClassic(doc, timesheet, logoDataUrl) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = 40;
  const right = pageWidth - 40;
  const mm = (v) => v; // use pt

  // Draw the full header for the first page
  drawPageHeader(doc, timesheet, logoDataUrl);

  // Table: rows per project, columns per day + total
  const days = buildWeek(timesheet);
  const dailyTotals = days.map(d => calcDayTotal(timesheet.entries, d));
  const headRow = [{ content: 'Project' }]
    .concat(days.map(d => d.toLocaleDateString(undefined,{ month:'short', day:'2-digit'})))
    .concat(['Totals']);
  
  // Group entries by project
  const entriesByProject = new Map(); // Map<projectName, Map<date, hours>>
  
  (timesheet.entries || []).forEach(entry => {
    const projectName = normalizeProject(entry) || 'No Project';
    const entryDate = new Date(entry.date || entry.entryDate);
    const dateKey = entryDate.toDateString();
    const hours = normalizeHours(entry);
    
    if (!entriesByProject.has(projectName)) {
      entriesByProject.set(projectName, new Map());
    }
    
    const projectEntries = entriesByProject.get(projectName);
    // Sum hours if multiple entries exist for same project on same day
    const existingHours = projectEntries.get(dateKey) || 0;
    projectEntries.set(dateKey, existingHours + hours);
  });
  
  // Build rows - one row per project showing hours across all days
  const bodyRows = [];
  const sortedProjects = Array.from(entriesByProject.keys()).sort();
  
  sortedProjects.forEach(projectName => {
    // Create a row for this project
    // Project name will wrap automatically based on cellWidth in columnStyles
    const tableRow = [projectName];
    const projectEntries = entriesByProject.get(projectName);
    let rowTotal = 0;
    
    days.forEach(day => {
      const dayKey = day.toDateString();
      const hours = projectEntries.get(dayKey) || 0;
      
      if (hours > 0) {
        tableRow.push(hours.toFixed(2));
        rowTotal += hours;
      } else {
        tableRow.push(''); // Empty if no hours for this project on this day
      }
    });
    
    // Total for this project across all days
    tableRow.push(rowTotal > 0 ? rowTotal.toFixed(2) : '0.00');
    bodyRows.push(tableRow);
  });
  
  const totalsRow = ['Total hrs']
    .concat(dailyTotals.map(v => v.toFixed(2)))
    .concat([(timesheet.entries || []).reduce((s,e)=>s+normalizeHours(e),0).toFixed(2)]);
  
  // If no projects found, add a message row
  if (bodyRows.length === 0) {
    bodyRows.push(['No entries', ...days.map(() => ''), '0.00']);
  }

  doc.autoTable({
    startY: CONTENT_START_Y,
    theme: 'grid',
    styles: { 
      font: 'helvetica', 
      fontSize: 9, 
      cellPadding: 6,
      textColor: 35, // Darker text for better visibility
      fillColor: [255, 255, 255] // White background for body cells
    },
    headStyles: { 
      fillColor: [245, 247, 250], // Light gray background for header
      textColor: 20, // Darker text for better visibility
      halign: 'center',
      fontStyle: 'bold'
    },
    bodyStyles: {
      fillColor: [250, 250, 252], // Very light background for table body
      textColor: 35, // Darker text for better visibility
      halign: 'center' // Center all body cell content by default
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255] // Alternate white rows for better readability
    },
    columnStyles: { 
      0: { cellWidth: mm(150), halign: 'left' }, // Project column left-aligned
      [headRow.length - 1]: { cellWidth: mm(70), halign: 'right' } // Totals column right-aligned
    },
    didParseCell: (data) => {
      // Make the "Project" header title left-aligned as well
      if (data.section === 'head' && data.column.index === 0) {
        data.cell.styles.halign = 'left';
      }
      // Enable text wrapping for project name cells (column 0 in body)
      if (data.section === 'body' && data.column.index === 0) {
        data.cell.styles.cellPadding = { top: 4, bottom: 4, left: 6, right: 6 };
        data.cell.styles.valign = 'top'; // Align multi-line text to top
      }
    },
    head: [headRow],
    body: bodyRows.concat([totalsRow]),
    didDrawPage: (data) => {
      // If it's not the first page, redraw the full header
      if (data.pageNumber > 1) {
        drawPageHeader(doc, timesheet, logoDataUrl);
        // Adjust the cursor Y for the table content on new pages
        data.cursor.y = CONTENT_START_Y;
      }
      // Draw footer on every page (page numbers will be added at the end if needed)
      drawPageFooter(doc, timesheet);
    },
  });

  // Entry list - Date, Project, Description with hours
  const afterTableY = (doc.lastAutoTable && doc.lastAutoTable.finalY ? doc.lastAutoTable.finalY : CONTENT_START_Y) + 40;
  let y = afterTableY;
  
  // Headers
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(20); // Darker for better visibility
  doc.text('Date', left, y);
  doc.text('Project', left + 60, y);
  doc.text('Description', left + 150, y);
  y += 24; // Increased spacing between headers and data entries
  
  // Build project name map for each entry
  const entryProjectMap = new Map();
  (timesheet?.entries || []).forEach(entry => {
    const projectName = normalizeProject(entry) || 'No Project';
    entryProjectMap.set(entry, projectName);
  });
  
  // Sort entries by date
  const sortedEntries = (timesheet?.entries || [])
    .sort((a, b) => {
      const dateA = new Date(a.date || a.entryDate);
      const dateB = new Date(b.date || b.entryDate);
      const dateCompare = dateA - dateB;
      if (dateCompare !== 0) return dateCompare;
      // If same date, sort by project name for consistency
      const projectA = normalizeProject(a) || '';
      const projectB = normalizeProject(b) || '';
      return projectA.localeCompare(projectB);
    });
  
  // Group entries by date to ensure complete days are rendered together
  const entriesByDay = new Map();
  sortedEntries.forEach(entry => {
    const entryDate = new Date(entry.date || entry.entryDate);
    const dayKey = entryDate.toDateString();
    if (!entriesByDay.has(dayKey)) {
      entriesByDay.set(dayKey, []);
    }
    entriesByDay.get(dayKey).push(entry);
  });
  
  // Display entries grouped by day
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(45); // Darker for better visibility
  
  const descriptionWidth = right - (left + 150);
  const projectMaxWidth = 90; // Maximum width for project column (between Date at left+60 and Description at left+150)
  const pageBottom = doc.internal.pageSize.getHeight() - 60;
  
  // Helper function to calculate total height needed for a day's entries
  function calculateDayHeight(dayEntries) {
    let totalHeight = 0;
    dayEntries.forEach(entry => {
      const projectName = entryProjectMap.get(entry) || 'No Project';
      const description = normalizeDescription(entry);
      const hours = normalizeHours(entry).toFixed(2);
      const descriptionWithHours = `${description} (${hours} hrs)`;
      const descLines = doc.splitTextToSize(descriptionWithHours, descriptionWidth);
      const projectLines = doc.splitTextToSize(projectName, projectMaxWidth);
      // Use the maximum height needed between project name and description
      const maxLines = Math.max(descLines.length, projectLines.length);
      totalHeight += maxLines * 12 + 8;
    });
    return totalHeight;
  }
  
  // Helper function to render headers on a new page for entry list
  function renderHeadersOnNewPage() {
    doc.addPage();
    // Draw the full page header (logo, company info, title, meta info)
    drawPageHeader(doc, timesheet, logoDataUrl);
    
    // Draw footer on new page
    drawPageFooter(doc, timesheet);
    
    // Position entry list headers after the full page header with additional top spacing
    // Full header ends around Y=216, add extra spacing then entry list headers
    y = CONTENT_START_Y + 30; // Start entry list section with additional top margin (30pt extra space for better separation)
    
    // Redraw task list specific headers (Date, Project, Description)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(20); // Darker for better visibility
    doc.text('Date', left, y);
    doc.text('Project', left + 60, y);
    doc.text('Description', left + 150, y);
    y += 24; // Space after headers
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(45); // Darker for better visibility
  }
  
  // Process each day's entries
  entriesByDay.forEach((dayEntries, dayKey) => {
    // Calculate total height needed for this entire day
    const dayHeight = calculateDayHeight(dayEntries);
    
    // Check if entire day fits on current page
    // If not, start a new page before rendering any entries for this day
    // Always check if we need a new page (remove the y > afterTableY condition)
    if (y + dayHeight > pageBottom) {
      renderHeadersOnNewPage();
    }
    
    // Render all entries for this day
      dayEntries.forEach(entry => {
        const dateStr = new Date(entry.date || entry.entryDate).toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
        const projectName = entryProjectMap.get(entry) || 'No Project';
        const description = normalizeDescription(entry);
        const hours = normalizeHours(entry).toFixed(2);
        const descriptionWithHours = `${description} (${hours} hrs)`;
      
      // Calculate text width for wrapping - both project name and description
      const projectLines = doc.splitTextToSize(projectName, projectMaxWidth);
      const descLines = doc.splitTextToSize(descriptionWithHours, descriptionWidth);
      // Use the maximum height needed between project name and description
      const maxLines = Math.max(projectLines.length, descLines.length);
      const requiredHeight = maxLines * 12 + 8;
      
      // Double-check individual entry (safety check in case a single entry is very large)
      if (y + requiredHeight > pageBottom) {
        renderHeadersOnNewPage();
      }
      
      // Render date on first line
      doc.text(dateStr, left, y);
      
      // Render project name with wrapping
      projectLines.forEach((line, idx) => {
        doc.text(line, left + 60, y + (idx * 12));
      });
      
      // Render description with wrapping
      descLines.forEach((line, idx) => {
        doc.text(line, left + 150, y + (idx * 12));
      });
      
      y += requiredHeight;
    });
  });
  
  // Add page numbers to all pages (only if multiple pages exist)
  const totalPages = doc.internal.getNumberOfPages();
  addPageNumbers(doc, totalPages);
}

function renderCompact(doc, timesheet, logoDataUrl) {
  // Simple variant: only totals row and flat task list
  const pageWidth = doc.internal.pageSize.getWidth();
  const left = 40; 
  const right = pageWidth - 40;
  
  // Add logo in top right corner
  if (logoDataUrl) {
    try {
      const logoWidth = 120;
      const logoHeight = 60;
      const logoX = pageWidth - logoWidth - 40;
      const logoY = 30;
      doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
    } catch (error) {
      console.warn('Failed to add logo to PDF:', error);
    }
  }
  
  doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Timesheet', left, 40);
  const days = buildWeek(timesheet);
  const dailyTotals = days.map(d => calcDayTotal(timesheet.entries, d));
  const headRow = [{content:''}].concat(days.map(d=>d.toLocaleDateString(undefined,{month:'short',day:'2-digit'}))).concat(['Totals']);
  const totalsRow = ['Total hrs']
    .concat(dailyTotals.map(v=>v.toFixed(2)))
    .concat([(timesheet.entries||[]).reduce((s,e)=>s+normalizeHours(e),0).toFixed(2)]);
  doc.autoTable({ 
    startY: 60, 
    head: [headRow], 
    body: [totalsRow],
    didDrawPage: (data) => {
      // Draw footer on every page (page numbers will be added at the end if needed)
      drawPageFooter(doc, timesheet);
    }
  });
  let y = (doc.lastAutoTable?.finalY || 80) + 10;
  doc.setFontSize(10); doc.setFont('helvetica','normal');
  (timesheet.entries||[]).forEach(e=>{
    const dateLabel = formatDateOnly(e.date) || e.date || '';
    const projectLabel = normalizeProject(e);
    const description = normalizeDescription(e);
    const hours = normalizeHours(e).toFixed(2);
    const t = `${dateLabel} ${projectLabel}`;
    const lines = doc.splitTextToSize(`${description} (${hours} hrs)`, right-(left+70));
    doc.text(t,left,y);
    doc.text(lines,left+70,y);
    y+= lines.length*12+6;
  });
  
  // Add page numbers to all pages (only if multiple pages exist)
  const totalPages = doc.internal.getNumberOfPages();
  addPageNumbers(doc, totalPages);
}

function normalizeHours(entry = {}) {
  if (typeof entry.hours === 'number') return entry.hours;
  if (typeof entry.hoursWorked === 'number') return entry.hoursWorked;
  const parsedHours = parseFloat(entry.hours);
  if (!Number.isNaN(parsedHours)) return parsedHours;
  const parsedWorked = parseFloat(entry.hoursWorked);
  if (!Number.isNaN(parsedWorked)) return parsedWorked;
  return 0;
}

function normalizeDescription(entry = {}) {
  return entry.description || entry.taskDescription || entry.notes || 'No description';
}

function normalizeProject(entry = {}) {
  if (typeof entry.project === 'string') return entry.project;
  if (entry.project && typeof entry.project.name === 'string') return entry.project.name;
  if (entry.projectName) return entry.projectName;
  return '';
}

function formatDateOnly(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}


